/**
 * Template Form Dialog
 * 
 * Create/Edit feedback templates with question selection and configuration.
 */

import React, { useState, useEffect } from 'react'
import { X, Plus, Trash2, GripVertical, Search } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import questionBankService, {
  FeedbackTemplate,
  Question,
  TemplateCreate,
  TemplateQuestionCreate,
  FeedbackContext,
  StakeholderRole,
} from '@/services/api/questionBankService'

interface TemplateFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  template?: FeedbackTemplate
  onSuccess: () => void
}

const FEEDBACK_CONTEXTS = [
  { value: 'MATRIX', label: 'Matrix Feedback' },
  { value: 'UPWARD_360', label: 'Upward 360°' },
  { value: 'EMPLOYEE_REQUESTED', label: 'Team Member Requested' },
  { value: 'PEER', label: 'Peer Feedback' },
  { value: 'PROJECT', label: 'Project Feedback' },
]

const STAKEHOLDER_ROLES = [
  { value: 'CAMPUS_GM', label: 'Campus GM' },
  { value: 'PROJECT_MANAGER', label: 'Project Manager' },
  { value: 'FACULTY_MEMBER', label: 'Faculty Member' },
  { value: 'DEPARTMENT_HEAD', label: 'Department Head' },
  { value: 'PEER', label: 'Peer' },
  { value: 'DIRECT_REPORT', label: 'Direct Report' },
  { value: 'EXTERNAL', label: 'External' },
  { value: 'STUDENT', label: 'Student' },
]

const TemplateFormDialog: React.FC<TemplateFormDialogProps> = ({
  open,
  onOpenChange,
  template,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false)
  const [availableQuestions, setAvailableQuestions] = useState<Question[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([])
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [requiredQuestionIds, setRequiredQuestionIds] = useState<Set<string>>(new Set())
  
  // Filters
  const [selectedTypes, setSelectedTypes] = useState<string[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedContexts, setSelectedContexts] = useState<string[]>([])
  const [formData, setFormData] = useState<Partial<TemplateCreate>>({
    template_name: '',
    template_code: '',
    template_type: 'in_the_moment',
    feedback_context: 'MATRIX',
    description: '',
    is_default: false,
    questions: [],
  })

  useEffect(() => {
    if (open) {
      loadQuestions()
      if (template) {
        // Convert API values (lowercase) to UI values (uppercase)
        setFormData({
          template_name: template.template_name || '',
          template_code: template.template_code || '',
          template_type: template.template_type ?? 'in_the_moment',
          feedback_context: template.feedback_context.toUpperCase() as FeedbackContext,
          description: template.description || '',
          primary_stakeholder_role: template.primary_stakeholder_role?.toUpperCase() as StakeholderRole,
          estimated_completion_time: template.estimated_completion_time,
          is_default: template.is_default ?? false,
        })
        // Extract question IDs from template_questions
        const questionIds = template.template_questions?.map(tq => tq.question_id) || []
        setSelectedQuestionIds(questionIds)
        
        // Pre-fill required questions
        const requiredIds = new Set(
          template.template_questions?.filter(tq => tq.is_required).map(tq => tq.question_id) || []
        )
        setRequiredQuestionIds(requiredIds)
      } else {
        resetForm()
      }
    }
  }, [template, open])

  const loadQuestions = async () => {
    try {
      // Every page: the picker used to offer only the first 100 questions
      const response = await questionBankService.listAllQuestions()
      setAvailableQuestions(response.items.filter(q => q.is_active))
      if (import.meta.env.DEV) console.log('✅ Loaded questions for template:', response.items.length)
    } catch (error) {
      console.error('Error loading questions:', error)
    }
  }

  const resetForm = () => {
    setFormData({
      template_name: '',
      template_code: '',
      template_type: 'in_the_moment',
      feedback_context: 'MATRIX',
      description: '',
      is_default: false,
    })
    setSelectedQuestionIds([])
    setSearchQuery('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Convert selected question IDs to TemplateQuestionCreate array
      const questions: TemplateQuestionCreate[] = selectedQuestionIds.map((questionId, index) => ({
        question_id: questionId,
        is_required: requiredQuestionIds.has(questionId),  // Use per-question required state
        display_order: index + 1,
      }))

      // Convert UI values (uppercase) to API values (lowercase)
      if (template) {
        // Update: Don't send template_code (immutable) but DO send questions
        const updateData = {
          template_name: formData.template_name!,
          template_type: formData.template_type,
          feedback_context: formData.feedback_context!.toLowerCase(),
          description: formData.description,
          primary_stakeholder_role: formData.primary_stakeholder_role?.toLowerCase(),
          estimated_completion_time: formData.estimated_completion_time,
          is_default: formData.is_default,
          questions,
        }

        if (import.meta.env.DEV) console.log('💾 Updating template:', updateData)
        await questionBankService.updateTemplate(template.id, updateData as any)
      } else {
        // Create: Include template_code and questions
        const createData = {
          template_name: formData.template_name!,
          template_code: formData.template_code!,
          template_type: formData.template_type,
          feedback_context: formData.feedback_context!.toLowerCase(),
          description: formData.description,
          primary_stakeholder_role: formData.primary_stakeholder_role?.toLowerCase(),
          estimated_completion_time: formData.estimated_completion_time,
          is_default: formData.is_default,
          questions,
        } as TemplateCreate

        if (import.meta.env.DEV) console.log('💾 Creating template:', createData)
        await questionBankService.createTemplate(createData)
      }

      onSuccess()
      onOpenChange(false)
      resetForm()
    } catch (error) {
      console.error('Error saving template:', error)
      alert('Error saving template. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const toggleQuestion = (questionId: string) => {
    if (selectedQuestionIds.includes(questionId)) {
      setSelectedQuestionIds(selectedQuestionIds.filter(id => id !== questionId))
    } else {
      setSelectedQuestionIds([...selectedQuestionIds, questionId])
    }
  }

  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null)
      return
    }
    
    const newQuestionIds = [...selectedQuestionIds]
    const draggedId = newQuestionIds[draggedIndex]
    
    // Remove from old position
    newQuestionIds.splice(draggedIndex, 1)
    
    // Insert at new position
    newQuestionIds.splice(dropIndex, 0, draggedId)
    
    setSelectedQuestionIds(newQuestionIds)
    setDraggedIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  // Get unique values for filters
  const availableTypes = Array.from(new Set(availableQuestions.map(q => q.question_type))).sort()
  const availableCategories = Array.from(new Set(availableQuestions.map(q => q.category))).sort()
  const availableContexts = Array.from(
    new Set(availableQuestions.flatMap(q => q.applicable_feedback_contexts || []))
  ).sort()

  const filteredQuestions = availableQuestions.filter(q => {
    const matchesSearch = !searchQuery ||
      q.question_text.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.question_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      q.category.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesType = selectedTypes.length === 0 || selectedTypes.includes(q.question_type)
    const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(q.category)
    const matchesContext = selectedContexts.length === 0 || 
      q.applicable_feedback_contexts?.some(ctx => selectedContexts.includes(ctx))
    
    return matchesSearch && matchesType && matchesCategory && matchesContext
  })

  // Maintain order from selectedQuestionIds
  const selectedQuestions = selectedQuestionIds
    .map(id => availableQuestions.find(q => q.id === id))
    .filter((q): q is Question => q !== undefined)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" key={template?.id || 'new'}>
        <DialogHeader>
          <DialogTitle>{template ? 'Edit Template' : 'Create New Template'}</DialogTitle>
          <DialogDescription>
            {template
              ? 'Update template details and question selection'
              : 'Create a new feedback template by selecting questions'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="template_name">Template Name *</Label>
                <Input
                  id="template_name"
                  value={formData.template_name}
                  onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                  placeholder="e.g., People Leader to Team Member - Annual Check-in"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="template_code">
                    Template Code *
                    {template && (
                      <span className="ml-2 text-xs text-amber-600 font-normal">
                        (Cannot be changed after creation)
                      </span>
                    )}
                  </Label>
                  <Input
                    id="template_code"
                    value={formData.template_code}
                    onChange={(e) => setFormData({ ...formData, template_code: e.target.value.toUpperCase() })}
                    placeholder="e.g., TMPL_MATRIX_MGR_EMP"
                    required
                    disabled={!!template}
                    className={template ? 'bg-slate-50 cursor-not-allowed' : ''}
                  />
                  {template && (
                    <p className="text-xs text-slate-500">
                      Template codes are immutable identifiers and cannot be modified after creation.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="feedback_context">Feedback Context *</Label>
                  <Select
                    key={`context-${open}-${template?.id || 'new'}`}
                    value={formData.feedback_context}
                    onValueChange={(value) => setFormData({ ...formData, feedback_context: value as FeedbackContext })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select context" />
                    </SelectTrigger>
                    <SelectContent>
                      {FEEDBACK_CONTEXTS.map((ctx) => (
                        <SelectItem key={ctx.value} value={ctx.value}>
                          {ctx.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="template_type">Distribution model *</Label>
                  <Select
                    key={`tt-${open}-${template?.id || 'new'}`}
                    value={formData.template_type ?? 'in_the_moment'}
                    onValueChange={(value) =>
                      setFormData({ ...formData, template_type: value as 'in_the_moment' | 'annual_campaign' })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_the_moment">In-the-Moment (per-request)</SelectItem>
                      <SelectItem value="annual_campaign">Annual Campaign (bulk)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Annual Campaign templates appear in the Feedback Campaigns admin tab and force
                    respondent-only visibility on responses.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stakeholder_role">Primary Stakeholder Role</Label>
                  <Select
                    key={`role-${open}-${template?.id || 'new'}`}
                    value={formData.primary_stakeholder_role}
                    onValueChange={(value) => setFormData({ ...formData, primary_stakeholder_role: value as StakeholderRole })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {STAKEHOLDER_ROLES.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="completion_time">Est. Completion Time (minutes)</Label>
                  <Input
                    id="completion_time"
                    type="number"
                    min={1}
                    value={formData.estimated_completion_time || ''}
                    onChange={(e) => setFormData({ ...formData, estimated_completion_time: e.target.value ? parseInt(e.target.value) : undefined })}
                    placeholder="15"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the purpose and use case for this template..."
                  rows={3}
                />
              </div>

              <div className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_default"
                    checked={formData.is_default}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked as boolean })}
                  />
                  <label htmlFor="is_default" className="text-sm cursor-pointer">
                    Default template for this context
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Question Selection */}
          <Card className="border-blue-200">
            <CardHeader className="bg-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Question Selection</CardTitle>
                  <CardDescription>
                    Select questions to include in this template ({selectedQuestionIds.length} selected)
                  </CardDescription>
                </div>
                <Badge variant="secondary">{availableQuestions.length} questions available</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-4">
                {/* Available Questions */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Available Questions</Label>
                    <div className="relative flex-1 max-w-xs">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                      <Input
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-8 h-8 text-sm"
                      />
                    </div>
                  </div>

                  {/* Filter Options */}
                  <div className="grid grid-cols-3 gap-2">
                    <Select
                      value={selectedTypes.length === 1 ? selectedTypes[0] : 'all'}
                      onValueChange={(value) => setSelectedTypes(value === 'all' ? [] : [value])}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        {availableTypes.map(type => (
                          <SelectItem key={type} value={type}>
                            {type.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={selectedCategories.length === 1 ? selectedCategories[0] : 'all'}
                      onValueChange={(value) => setSelectedCategories(value === 'all' ? [] : [value])}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {availableCategories.map(cat => (
                          <SelectItem key={cat} value={cat}>
                            {cat.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={selectedContexts.length === 1 ? selectedContexts[0] : 'all'}
                      onValueChange={(value) => setSelectedContexts(value === 'all' ? [] : [value])}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="All Contexts" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Contexts</SelectItem>
                        {availableContexts.map(ctx => (
                          <SelectItem key={ctx} value={ctx}>
                            {ctx.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span>Showing {filteredQuestions.length} of {availableQuestions.length} questions</span>
                    {(selectedTypes.length > 0 || selectedCategories.length > 0 || selectedContexts.length > 0) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() => {
                          setSelectedTypes([])
                          setSelectedCategories([])
                          setSelectedContexts([])
                          setSearchQuery('')
                        }}
                      >
                        Clear filters
                      </Button>
                    )}
                  </div>

                  <ScrollArea className="h-96 rounded border border-slate-200 bg-white">
                    <div className="p-2 space-y-1">
                      {filteredQuestions.map((question) => {
                        const isSelected = selectedQuestionIds.includes(question.id)
                        return (
                          <div
                            key={question.id}
                            className={`p-3 rounded border cursor-pointer transition-colors ${
                              isSelected
                                ? 'bg-blue-50 border-blue-200'
                                : 'hover:bg-slate-50 border-slate-200'
                            }`}
                            onClick={() => toggleQuestion(question.id)}
                          >
                            <div className="flex items-start gap-2">
                              <Checkbox checked={isSelected} className="mt-1" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-xs">
                                    {question.question_type.replace(/_/g, ' ')}
                                  </Badge>
                                  <Badge className="text-xs">{question.category}</Badge>
                                </div>
                                <p className="text-sm text-slate-900 line-clamp-2">{question.question_text}</p>
                                <p className="text-xs text-slate-500 mt-1">{question.question_code}</p>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </ScrollArea>
                </div>

                {/* Selected Questions */}
                <div className="space-y-3">
                  <Label>Selected Questions ({selectedQuestions.length})</Label>
                  <ScrollArea className="h-96 rounded border border-slate-200 bg-slate-50">
                    <div className="p-2 space-y-1">
                      {selectedQuestions.length === 0 ? (
                        <div className="text-center py-12 text-slate-500 text-sm">
                          No questions selected yet
                        </div>
                      ) : (
                        selectedQuestions.map((question, index) => (
                          <div
                            key={question.id}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`p-3 rounded border border-slate-200 bg-white transition-all ${
                              draggedIndex === index ? 'opacity-50 scale-95 border-blue-400' : 'opacity-100 scale-100'
                            } cursor-move hover:border-blue-300`}
                          >
                            <div className="flex items-start gap-2">
                              <div className="flex items-center gap-2 mt-1">
                                <GripVertical className="h-4 w-4 text-slate-400 cursor-grab active:cursor-grabbing" />
                                <span className="text-xs font-mono text-slate-500">#{index + 1}</span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-xs">
                                    {question.question_type.replace(/_/g, ' ')}
                                  </Badge>
                                  <Badge className="text-xs">{question.category}</Badge>
                                </div>
                                <p className="text-sm text-slate-900 line-clamp-2 mb-2">{question.question_text}</p>
                                {/* Required checkbox */}
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    id={`required-${question.id}`}
                                    checked={requiredQuestionIds.has(question.id)}
                                    onCheckedChange={(checked) => {
                                      const newSet = new Set(requiredQuestionIds)
                                      if (checked) {
                                        newSet.add(question.id)
                                      } else {
                                        newSet.delete(question.id)
                                      }
                                      setRequiredQuestionIds(newSet)
                                    }}
                                  />
                                  <label
                                    htmlFor={`required-${question.id}`}
                                    className="text-xs text-slate-600 cursor-pointer"
                                  >
                                    Required question
                                  </label>
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleQuestion(question.id)}
                                className="h-8 w-8 p-0"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </CardContent>
          </Card>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !selectedQuestionIds.length}>
              {loading ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default TemplateFormDialog
